/**
 * Workflows list — status + training-mode toggle.
 *
 * The full authoring wizard lives at `/workflows/new` and
 * `/workflows/:id/edit`; this screen is a supervisor-facing list that
 * lets you:
 *   - See which workflows are in each maturity state.
 *   - Toggle training mode (per-turn audit envelope for break-in
 *     review). SUPERVISOR+ required.
 *
 * Loading / empty / error / permission-denied states per Rule Zero #8.
 */
import { useMemo, useState } from "react";
import { AlertCircle, GraduationCap, Loader2, Play, Wand2, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useEntities } from "@/lib/hooks/useEntities";
import { StatusPill } from "@/components/ui/StatusPill";
import { ApiError } from "@/lib/apiClient";

import {
  type Workflow,
  startTrainingRun,
  useSetTrainingMode,
  useWorkflows,
} from "./api";


const STATE_FILTER_OPTS: { value: string; label: string }[] = [
  { value: "all", label: "All states" },
  { value: "draft", label: "Draft" },
  { value: "testing", label: "Testing" },
  { value: "breaking_in", label: "Breaking-in" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "retired", label: "Retired" },
];


function maturityStatus(state: string): "running" | "approval" | "success" | "failed" | "queued" | "human" | "takeover" {
  switch (state) {
    case "active":
      return "success";
    case "breaking_in":
    case "testing":
      return "approval";
    case "paused":
    case "retired":
      return "failed";
    default:
      return "queued";
  }
}


export function WorkflowsScreen() {
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [flash, setFlash] = useState<string | null>(null);
  const [trainTarget, setTrainTarget] = useState<Workflow | null>(null);
  const navigate = useNavigate();
  const entitiesQuery = useEntities();
  const entities = entitiesQuery.data?.items ?? [];

  const { data, isLoading, error, refetch } = useWorkflows({
    entity: entityFilter === "all" ? undefined : entityFilter,
    maturityState: stateFilter === "all" ? undefined : stateFilter,
  });

  const workflows = useMemo(() => data?.items ?? [], [data]);
  const stats = useMemo(() => {
    const training = workflows.filter((w) => w.trainingMode).length;
    return { total: workflows.length, training };
  }, [workflows]);

  const flashMessage = (text: string) => {
    setFlash(text);
    window.setTimeout(() => setFlash((f) => (f === text ? null : f)), 3500);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-line bg-paper-0 px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold text-ink-1">Workflows</h1>
          <p className="text-xs text-ink-3">
            {stats.total} total · {stats.training} in training mode
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1"
            aria-label="Filter by entity"
          >
            <option value="all">All entities</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1"
            aria-label="Filter by maturity state"
          >
            {STATE_FILTER_OPTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Link
            to="/workflows/new"
            className="inline-flex items-center gap-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 hover:bg-ink-0"
          >
            <Wand2 size={14} />
            New
          </Link>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading && (
          <div className="flex items-center gap-2 py-12 text-ink-3">
            <Loader2 className="animate-spin" size={18} />
            Loading workflows…
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-start gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-4 text-sm">
            <AlertCircle className="text-status-failed" size={18} />
            <div>
              <div className="font-medium text-ink-1">Couldn't load workflows.</div>
              <div className="text-ink-3">{(error as Error).message}</div>
              {error instanceof ApiError && error.correlationId && (
                <div className="mt-1 font-mono text-[10px] text-ink-3">
                  {error.correlationId}
                </div>
              )}
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-2 rounded-md border border-paper-line bg-paper-0 px-2 py-1 text-xs"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && workflows.length === 0 && (
          <div className="rounded-md border border-dashed border-paper-line p-8 text-center text-ink-3">
            <GraduationCap className="mx-auto mb-2" size={24} />
            No workflows match these filters. Start one from the wizard.
          </div>
        )}

        {!isLoading && !error && workflows.length > 0 && (
          <ul className="divide-y divide-paper-line rounded-md border border-paper-line bg-paper-0">
            {workflows.map((w) => (
              <WorkflowRow
                key={w.id}
                workflow={w}
                onFlash={flashMessage}
                onTrain={() => setTrainTarget(w)}
              />
            ))}
          </ul>
        )}
      </main>

      {trainTarget && (
        <TrainModal
          workflow={trainTarget}
          onClose={() => setTrainTarget(null)}
          onLaunched={(jobId) => {
            setTrainTarget(null);
            flashMessage(`Training run queued — opening replay.`);
            navigate(`/jobs/${jobId}/replay`);
          }}
          onError={(msg) => flashMessage(msg)}
        />
      )}

      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 rounded-md bg-ink-1 px-4 py-2 text-sm text-paper-0 shadow-lg"
        >
          {flash}
        </div>
      )}
    </div>
  );
}


function WorkflowRow(props: {
  workflow: Workflow;
  onFlash: (msg: string) => void;
  onTrain: () => void;
}) {
  const { workflow } = props;
  const setTraining = useSetTrainingMode(workflow.id);

  const toggle = () => {
    const next = !workflow.trainingMode;
    setTraining.mutate(next, {
      onSuccess: () =>
        props.onFlash(
          next
            ? `Training mode enabled for ${workflow.name}.`
            : `Training mode disabled for ${workflow.name}.`,
        ),
      onError: (err) =>
        props.onFlash(`Couldn't toggle training mode: ${(err as Error).message}`),
    });
  };

  return (
    <li className="flex items-center justify-between px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-ink-1">{workflow.name}</span>
          <span className="rounded-sm bg-paper-1 px-1.5 py-0.5 text-[10px] text-ink-3">
            {workflow.entityCode}
          </span>
          <StatusPill
            status={maturityStatus(workflow.maturityState)}
            label={workflow.maturityState.replace("_", " ")}
          />
          {workflow.trainingMode && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-status-approval/10 px-1.5 py-0.5 text-[10px] text-status-approval">
              <GraduationCap size={10} /> training
            </span>
          )}
        </div>
        {workflow.description && (
          <div className="mt-0.5 truncate text-xs text-ink-3">{workflow.description}</div>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
          <span>v{workflow.currentVersion}</span>
          <span>
            · {workflow.breakInSuccessfulRuns}/{workflow.breakInTarget} break-in
          </span>
          <span>· {workflow.runnerTier}</span>
        </div>
      </div>
      <div className="ml-3 flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          disabled={setTraining.isPending}
          title={
            workflow.trainingMode
              ? "Disable per-turn supervisor review"
              : "Enable per-turn supervisor review during break-in"
          }
          className={
            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-50 " +
            (workflow.trainingMode
              ? "border-status-approval/40 bg-status-approval/10 text-status-approval hover:bg-status-approval/20"
              : "border-paper-line bg-paper-0 text-ink-2 hover:bg-paper-1")
          }
        >
          {setTraining.isPending ? (
            <Loader2 className="animate-spin" size={12} />
          ) : (
            <GraduationCap size={12} />
          )}
          {workflow.trainingMode ? "Training on" : "Training off"}
        </button>
        <button
          type="button"
          onClick={props.onTrain}
          title="Queue a training run — enables training mode, queues a test-mode job in full agent runtime, opens the replay"
          className="inline-flex items-center gap-1 rounded-md border border-teal-500/40 bg-teal-500/10 px-2 py-1 text-xs text-teal-600 transition-colors hover:bg-teal-500/20"
        >
          <Play size={12} />
          Train
        </button>
      </div>
    </li>
  );
}


function TrainModal(props: {
  workflow: Workflow;
  onClose: () => void;
  onLaunched: (jobId: string) => void;
  onError: (msg: string) => void;
}) {
  const { workflow } = props;
  const [inputs, setInputs] = useState("{}");
  const [enableTraining, setEnableTraining] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(inputs || "{}");
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error("inputs must be a JSON object");
      }
    } catch (err) {
      setParseError((err as Error).message);
      return;
    }
    setParseError(null);
    setSubmitting(true);
    try {
      const job = await startTrainingRun({
        workflow,
        inputs: parsed,
        enableTrainingMode: enableTraining,
      });
      props.onLaunched(job.id);
    } catch (err) {
      props.onError(`Couldn't launch training run: ${(err as Error).message}`);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-1/30 p-4">
      <div className="w-full max-w-lg rounded-lg border border-paper-line bg-paper-0 p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-ink-1">Train this workflow</h3>
            <p className="mt-0.5 text-xs text-ink-3">
              {workflow.name} · {workflow.entityCode} · v{workflow.currentVersion}
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-3 hover:bg-paper-1"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="flex items-center gap-2 text-xs text-ink-2">
            <input
              type="checkbox"
              checked={enableTraining}
              onChange={(e) => setEnableTraining(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <span>
              Enable training mode on this workflow
              {workflow.trainingMode && (
                <span className="ml-1 text-ink-3">(already on)</span>
              )}
            </span>
          </label>

          <label className="block text-xs text-ink-3">
            <span>Inputs (JSON object)</span>
            <textarea
              value={inputs}
              onChange={(e) => setInputs(e.target.value)}
              rows={6}
              spellCheck={false}
              className="mt-1 w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 font-mono text-xs text-ink-1"
            />
          </label>

          <div className="rounded-md bg-paper-1 p-3 text-xs text-ink-2">
            Launches a <strong className="text-ink-1">full-mode</strong>{" "}
            test-run against the real agent runtime. Training mode
            emits a per-turn audit envelope so a supervisor reviews
            every action. The replay opens automatically.
          </div>

          {parseError && (
            <div className="rounded-md bg-status-failed/10 p-2 text-xs text-status-failed">
              {parseError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={props.onClose}
              className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1 rounded-md bg-teal-500 px-3 py-1.5 text-sm text-paper-3 hover:bg-teal-600 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <Play size={14} />
              )}
              Launch training run
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
